import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../shared/components/MainLayout';
import api from '../../../shared/utils/api';

type StatsCardProps = {
    title: string;
    desc: string;
    icon: string;
    onClick?: () => void;
};

function StatsCard({ title, desc, icon, onClick }: StatsCardProps) {
    return (
        <div 
            className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
            onClick={onClick}
        >
            <div className="text-2xl mb-2">{icon}</div>
            <h3 className="font-semibold text-gray-800 mb-1 text-base">{title}</h3>
            <p className="text-xs text-gray-600">{desc}</p>
        </div>
    );
}

const Main_Accountant: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalCustomers: 0,
        totalUsers: 0
    });
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        try {
            console.log('🔥 Fetching real stats from API...');
            console.log('🔥 API endpoint: /api/customerslist/accountant-stats');
            
            const response = await api.getAccountantStats();
            console.log('🔥 Full API response:', response);
            
            if (response && response.success && response.data) {
                console.log('✅ Stats fetched successfully:', response.data);
                setStats({
                    totalCustomers: response.data.totalCustomers || 0,
                    totalUsers: response.data.totalUsers || 0
                });
            } else {
                console.error('❌ Stats API response failed or invalid:', response);
                console.error('❌ Response structure:', {
                    success: response?.success,
                    data: response?.data,
                    message: response?.message
                });
                // Fallback to mock data if API fails
                setStats({
                    totalCustomers: 0,
                    totalUsers: 0
                });
            }
        } catch (error) {
            console.warn('⚠️ Error fetching stats, using fallback:', error);
            
            // Try to get some test data to verify API connection
            try {
                console.log('🔥 Trying to fetch customer list as test...');
                const testResponse = await api.get('/api/customerslist/accountant/customers');
                console.log('🔥 Test customer list response:', testResponse);
                
                if (testResponse && testResponse.success && testResponse.data) {
                    const customerCount = testResponse.data.length || 0;
                    console.log('🔥 Found customers from test call:', customerCount);
                    setStats({
                        totalCustomers: customerCount,
                        totalUsers: 0 // We'll need a separate call for users
                    });
                } else {
                    setStats({
                        totalCustomers: 0,
                        totalUsers: 0
                    });
                }
            } catch (testError) {
                console.error('❌ Test API call also failed:', testError);
                setStats({
                    totalCustomers: 0,
                    totalUsers: 0
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const handleViewCustomers = () => {
        navigate('/main_accountant/customers');
    };

    const handleViewUsers = () => {
        navigate('/main_accountant/app-user');
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Уншиж байна...</span>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="flex flex-col flex-1 min-h-0 p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Нэвтэрсэн</h1>
                    <p className="text-gray-600">Таны мэдээллийн дэлгэрэнгүй</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <StatsCard
                        title={`${stats.totalCustomers} Харилцагч`}
                        desc="Нийт харилцагчийн тоо"
                        icon="👥"
                        onClick={handleViewCustomers}
                    />
                    <StatsCard
                        title={`${stats.totalUsers} Хэрэглэгч`}
                        desc="Нийт хэрэглэгчийн тоо"
                        icon="👤"
                        onClick={handleViewUsers}
                    />
                </div>

                <div className="text-center">
                    <p className="text-gray-600 mb-4">Дэлгэрэнгүй жагсаалтыг харахын тулд дээрх картуудыг дарна уу</p>
                </div>
            </div>
        </MainLayout>
    );
};

export default Main_Accountant;